import {Card, CardContent, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch, TextField, Button, Link, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, CircularProgress} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton'
import React, {useEffect} from "react";
import CustomTable from "../core/CustomTable";
import moment from "moment";
import {pushDataToJarvis, saveEditedPMLData, applyDiscounts ,getCMBulk } from "../../utils/API";
import {showToast} from "../../utils/tostr";
import {isInsurerUser, isNotInsurerReadOnlyUser} from "../../constants/UserAuth";

function PMLTab(props) {
    const {claimData, PMLTableColumn, showProportional} = props;

    const [editedPMLRows, setEditedPMLRows] = React.useState([])
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const [isDiscountSubmitting, setIsDiscountSubmitting] = React.useState(false)
    const [hasCappedApprovedAmt, setHasCappedApprovedAmt] = React.useState(false);

    const discountTypeMap = {
        'SPOT': 'SPOT Discount',
        'CM_SMALL': 'CM SMALL Discount',
        'CM_BULK': 'CM Bulk Discount'
    };

    const getInitialDiscounts = () => {
        const apiDiscounts = claimData?.discountDetails || [];
        const defaultDiscounts = [
            { id: 1, discount_type: 'SPOT', type: 'SPOT Discount', status: false, amount: 0, percentage: 0 },
            { id: 2, discount_type: 'CM_SMALL', type: 'CM Small Discount', status: false, amount: 0, percentage: 0 },
            { id: 3, discount_type: 'CM_BULK', type: 'CM Bulk Discount', status: false, amount: 0, percentage: 0 }
        ];

        return defaultDiscounts.map(defaultDiscount => {
            const apiDiscount = apiDiscounts.find(d => d.discount_type === defaultDiscount.discount_type);
            if (apiDiscount) {
                return {
                    ...defaultDiscount,
                    status: apiDiscount.enable ?? true,
                    amount: apiDiscount.discount_amount || 0,
                    percentage: apiDiscount.discount_percentage || 0
                };
            }
            return defaultDiscount;
        });
    };

    const [discounts, setDiscounts] = React.useState(getInitialDiscounts());
    const [cmBulkModalOpen, setCmBulkModalOpen] = React.useState(false);
    const [cmBulkData, setCmBulkData] = React.useState(null);
    const [isCmBulkLoading, setIsCmBulkLoading] = React.useState(false);

    const pushToJarvis = async () => {
        try {
            setIsSubmitting(true)
            await saveEditedPMLData(claimData.id, localStorage.getItem("email"), editedPMLRows.map(item => item.value), claimData.intimationNumber)
            await pushDataToJarvis(claimData.id, localStorage.getItem("email"), claimData.intimationNumber)
            setIsSubmitting(false)
            showToast('Data pushed to Jarvis', 'success', 'push-success')
        } catch (e) {
            showToast('Data push Failed', 'error', 'push-failure')
            setIsSubmitting(false)
        }
    }

    const handleToggleDiscount = (id) => {
        setDiscounts(prevDiscounts => {
            // Enforce only one of CM Small or CM Bulk can be active at a time
            const target = prevDiscounts.find(d => d.id === id);
            if (target && !target.status) {
                const otherType = target.discount_type === 'CM_SMALL' ? 'CM_BULK'
                    : target.discount_type === 'CM_BULK' ? 'CM_SMALL'
                        : null;
                const other = otherType && prevDiscounts.find(d => d.discount_type === otherType);
                if (other && other.status) {
                    showToast('Cannot apply CM Small Discount and CM Bulk Discount at the same time', 'warning', 'discount-mutual-exclusive-warning');
                    return prevDiscounts;
                }
            }

            const updatedDiscounts = prevDiscounts.map(discount => {
                // Toggle the selected discount
                if (discount.id === id) {
                    return { ...discount, status: !discount.status };
                }
                return discount;
            });

            const approvedAmount = claimData?.pmlClaimResult?.claim_approved_amount || 0;
            // Calculate total discount amount only from active discounts
            const totalDiscountAmount = updatedDiscounts
                .filter(d => d.status)
                .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
            // If total exceeds approved amount, reset the toggled row's amount and show warning
            if (totalDiscountAmount > approvedAmount) {
                showToast('Total discount amount cannot exceed the approved amount. Amount has been reset.', 'warning', 'discount-toggle-exceed-warning');
                return updatedDiscounts.map(discount => {
                    if (discount.id === id && discount.status) {
                        // Reset amount and percentage for the toggled row
                        return { ...discount, amount: 0, percentage: 0 };
                    }
                    if (discount.status && Number(discount.amount) > 0) {
                        const percentage = approvedAmount > 0 ? (Number(discount.amount) / approvedAmount) * 100 : 0;
                        return { ...discount, percentage: percentage };
                    }
                    return { ...discount, percentage: 0 };
                });
            }
            // Recalculate percentages for all active discounts
            return updatedDiscounts.map(discount => {
                if (discount.status && Number(discount.amount) > 0) {
                    const percentage = approvedAmount > 0 ? (Number(discount.amount) / approvedAmount) * 100 : 0;
                    return { ...discount, percentage: percentage };
                }
                return { ...discount, percentage: 0 };
            });
        });
    }

    const handleAmountChange = (id, value) => {
        // Allow the field to be cleared; treat it as 0 for calculations until the user types a value
        const numValue = value === '' ? '' : (parseFloat(value) || 0);

        setDiscounts(prevDiscounts => {
            const updatedDiscounts = prevDiscounts.map(discount => {
                if (discount.id === id) {
                    return { ...discount, amount: numValue };
                }
                return discount;
            });

            // Calculate total discount amount only from active (status = true) discounts
            const totalDiscountAmount = updatedDiscounts
                .filter(d => d.status)
                .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
            const approvedAmount = claimData?.pmlClaimResult?.claim_approved_amount || 0;

            // Validate that total discount doesn't exceed approved amount
            if (totalDiscountAmount > approvedAmount) {
                showToast('Total discount amount cannot exceed the approved amount', 'warning', 'discount-exceed-warning');
                // Revert the change
                return prevDiscounts;
            }

            // Calculate percentage for each discount against the total approved amount
            return updatedDiscounts.map(discount => {
                if (discount.status && Number(discount.amount) > 0) {
                    const percentage = approvedAmount > 0 ? (Number(discount.amount) / approvedAmount) * 100 : 0;
                    return { ...discount, percentage: percentage };
                }
                return { ...discount, percentage: 0 };
            });
        });
    }

    // An enabled discount must carry an amount greater than zero
    const isAmountInvalid = (discount) => discount.status && !(Number(discount.amount) > 0);

    const handleSaveDiscounts = async () => {
        const invalidDiscounts = discounts.filter(isAmountInvalid);
        if (invalidDiscounts.length > 0) {
            showToast(
                `Discount amount cannot be zero for ${invalidDiscounts.map(d => d.type).join(', ')}`,
                'warning',
                'discount-zero-amount-warning'
            );
            return;
        }

        try {
            setIsDiscountSubmitting(true);

            const discountTypes = discounts.map(d => ({
                discount_type: d.discount_type,
                discount_percentage: parseFloat(d.percentage.toFixed(2)),
                discount_amount: Number(d.amount) || 0,
                enable: d.status
            }));

            // if (!discounts.some(d => d.status)) {
            //     showToast('Please enable at least one discount', 'warning', 'discount-warning');
            //     setIsDiscountSubmitting(false);
            //     return;
            // }

            const requestBody = {
                claimId: claimData.id || 0,
                claim_amount: claimData?.pmlClaimResult?.claim_approved_amount || null,
                discountTypes: discountTypes
            };

            await applyDiscounts(requestBody);
            setIsDiscountSubmitting(false);
            showToast('Discounts applied successfully', 'success', 'discount-save-success');

            // Optionally reload claim data here to get updated discount details
            // props.reloadClaimData && props.reloadClaimData();

        } catch (e) {
            console.error('Failed to apply discounts:', e);
            showToast('Failed to apply discounts', 'error', 'discount-save-error');
            setIsDiscountSubmitting(false);
        }
    }


    const handleCMBulk = async () => {
        try {
            setIsCmBulkLoading(true);
            setCmBulkModalOpen(true);
            const response = await getCMBulk(claimData.id);
            setCmBulkData(response?.data || null);
            setIsCmBulkLoading(false);
        } catch (e) {
            console.error('Failed to fetch CM Bulk data:', e);
            showToast('Failed to fetch CM Bulk data', 'error', 'cm-bulk-fetch-error');
            setIsCmBulkLoading(false);
            setCmBulkModalOpen(false);
        }
    }

    const handleCloseCmBulkModal = () => {
        setCmBulkModalOpen(false);
        setCmBulkData(null);
    }


    useEffect(() => {
        setHasCappedApprovedAmt(
            claimData?.claimPolicyAttributes?.stpRuleAttributes?.amountCapped !== undefined &&
            claimData?.claimPolicyAttributes?.stpRuleAttributes?.amountCapped !== null &&
            claimData?.claimPolicyAttributes?.stpRuleAttributes?.amountCapped === true
        );
    }, [claimData?.pmlClaimResult?.cappedApprovedAmt]);

    useEffect(() => {
        setDiscounts(getInitialDiscounts());
    }, [claimData?.discountDetails]);


    return (
        <Paper>
            <Grid style={{marginTop: "10px", marginBottom: "15px"}}>
                <CustomTable
                    tableColumns={PMLTableColumn || []}
                    tableData={
                        claimData.tariffPmlDataMerged?.billTariffPmlAmountList || []
                    }
                    isPagination={false}
                    isSearch={true}
                    isPML={true}
                    showRoomProportional={true}
                    noStickyHeader={true}
                    setEditedPMLRows={setEditedPMLRows}
                />
            </Grid>

        <Card style={{ padding: 10, marginBottom: "35px" }}>
            <Grid
                display={"flex"}
                justifyContent={"flex-end"}
                alignItems={"center"}
                gap={"20px"}
                style={{ marginRight: "1rem" }}
            >
                {/* Base SI */}
                <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold"
                        }}
                    >
                        Base SI :
                    </Typography>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            paddingLeft: "0.5rem",
                            borderRight: "1px solid #ccc",
                            paddingRight: "1rem"
                        }}
                    >
                        &#x20b9;{"  "}
                        {
                            claimData?.pmlClaimResult?.base_si === undefined ||
                            claimData?.pmlClaimResult?.base_si === null
                                ? " 0"
                                : claimData.pmlClaimResult.base_si.toLocaleString("en-IN")
                        }
                    </Typography>
                </Grid>

                {/* CB Balance */}
                <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold"
                        }}
                    >
                        CB Balance :
                    </Typography>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            paddingLeft: "0.5rem",
                            borderRight: "1px solid #ccc",
                            paddingRight: "1rem"
                        }}
                    >
                        &#x20b9;{"  "}
                        {
                            claimData?.pmlClaimResult?.cb_balance === undefined ||
                            claimData?.pmlClaimResult?.cb_balance === null
                                ? " 0"
                                : claimData.pmlClaimResult.cb_balance.toLocaleString("en-IN")
                        }
                    </Typography>
                </Grid>

                {/* Reload Balance */}
                <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold"
                        }}
                    >
                        Reload Balance :
                    </Typography>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            paddingLeft: "0.5rem",
                            borderRight: "1px solid #ccc",
                            paddingRight: "1rem"
                        }}
                    >
                        &#x20b9;{"  "}
                        {
                            claimData?.pmlClaimResult?.reload_balance === undefined ||
                            claimData?.pmlClaimResult?.reload_balance === null
                                ? " 0"
                                : claimData.pmlClaimResult.reload_balance.toLocaleString("en-IN")
                        }
                    </Typography>
                </Grid>

                {/* Admissible Amount before SI check */}
                <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold"
                        }}
                    >
                        Admissible amount before SI check :
                    </Typography>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            paddingLeft: "0.5rem",
                            borderRight: "1px solid #ccc",
                            paddingRight: "1rem"
                        }}
                    >
                        &#x20b9;{"  "}
                        {
                            claimData?.pmlClaimResult?.amount_before_avaiaable_si_check === undefined ||
                            claimData?.pmlClaimResult?.amount_before_avaiaable_si_check === null
                                ? " 0"
                                : claimData.pmlClaimResult.amount_before_avaiaable_si_check.toLocaleString("en-IN")
                        }
                    </Typography>
                </Grid>

                {/* Available SI */}
                <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold"
                        }}
                    >
                        Available SI :
                    </Typography>
                    <Typography
                        variant="subTitle1"
                        component={"p"}
                        sx={{
                            fontSize: "14px",
                            paddingLeft: "0.5rem",
                            borderRight: "1px solid #ccc",
                            paddingRight: "1rem"
                        }}
                    >
                        &#x20b9;{"  "}
                        {
                            claimData?.pmlClaimResult?.total_considered_si === undefined ||
                            claimData?.pmlClaimResult?.total_considered_si === null
                                ? " 0"
                                : claimData.pmlClaimResult.total_considered_si.toLocaleString("en-IN")
                        }
                    </Typography>
                </Grid>

                {/* Sublimit Amount */}
                {claimData?.pmlClaimResult?.procedure_sublimit_applicable > 0 && (
                    <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            variant="subTitle1"
                            component={"p"}
                            sx={{ fontSize: "14px", fontWeight: "bold" }}
                        >
                            Sublimit Amount :
                        </Typography>
                        <Typography
                            variant="subTitle1"
                            component={"p"}
                            sx={{
                                fontSize: "14px",
                                paddingLeft: "0.5rem",
                                borderRight: "1px solid #ccc",
                                paddingRight: "1rem"
                            }}
                        >
                            &#x20b9;{"  "}
                            {
                                claimData?.pmlClaimResult?.procedure_sublimit_applicable === undefined ||
                                claimData?.pmlClaimResult?.procedure_sublimit_applicable === null
                                    ? " 0"
                                    : claimData.pmlClaimResult.procedure_sublimit_applicable.toLocaleString("en-IN")
                            }
                        </Typography>
                    </Grid>
                )}

                {/* Amount Approved */}
                <Grid item xs={6} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            variant="subTitle1"
                            component={"p"}
                            sx={{ fontSize: "14px", fontWeight: "bold" }}
                        >
                            Amount Approved :
                        </Typography>
                        <Typography
                            variant="subTitle1"
                            component={"p"}
                            sx={{
                                fontSize: "14px",
                                paddingLeft: "0.5rem"
                            }}
                        >
                            &#x20b9;{"  "}
                            {
                                claimData?.pmlClaimResult?.claim_approved_amount === undefined ||
                                claimData?.pmlClaimResult?.claim_approved_amount === null
                                    ? " 0"
                                    : claimData.pmlClaimResult.claim_approved_amount.toLocaleString("en-IN")
                            }
                        </Typography>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            variant="subTitle1"
                            component={"p"}
                            sx={{ fontSize: "14px", fontWeight: "bold" }}
                        >
                             Amount After Discount :
                        </Typography>
                        <Typography
                            variant="subTitle1"
                            component={"p"}
                            sx={{
                                fontSize: "14px",
                                paddingLeft: "0.5rem"
                            }}
                        >
                            &#x20b9;{"  "}
                            {
                                claimData?.tariffPmlDataMerged?.amount_after_discount === undefined ||
                                claimData?.tariffPmlDataMerged?.amount_after_discount === null
                                    ? " 0"
                                    : claimData.tariffPmlDataMerged.amount_after_discount.toLocaleString("en-IN")
                            }
                        </Typography>
                    </div>
                </Grid>

                {
                    hasCappedApprovedAmt && (
                        <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                            <Typography
                                variant="subTitle1"
                                component={"p"}
                                sx={{ fontSize: "14px", fontWeight: "bold" }}
                            >
                                STP Capped Amount :
                            </Typography>
                            <Typography
                                variant="subTitle1"
                                component={"p"}
                                sx={{ fontSize: "14px" }}
                            >
                                &#x20b9;{"  "}
                                {
                                    claimData?.amountApproved.toLocaleString("en-IN")
                                }
                            </Typography>
                        </Grid>
                    )

                }

                {/* Push to Jarvis Button */}
                <Grid item xs={12} style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
                    <LoadingButton
                        size="small"
                        loading={isSubmitting}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        variant="contained"
                        onClick={pushToJarvis}
                    >
                        Push to Jarvis
                    </LoadingButton>
                </Grid>
            </Grid>
        </Card>


     {claimData?.claimStatus == 'DISCHARGE_RESPONSE_SENT' && (
     <Card sx={{ minWidth: 275, marginBottom: "20px" ,width: '50%'}}>
        <CardContent>
            <Typography
                sx={{
                    fontSize: 20,
                    fontWeight: 900,
                    fontFamily: '"Nunito", sans-serif',
                    marginBottom: 2
                }}
            >
                Discount Management
            </Typography>

            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    width: '200px'
                                }}
                            >
                                Discount Type
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    width: '100px',
                                    textAlign: 'center'
                                }}
                            >
                                Status
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    width: 'auto'
                                }}
                            >
                                Deducted Amount %
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {discounts.map((discount) => (
                            <TableRow key={discount.id}>
                                <TableCell sx={{ width: '200px' }}>
                                    <Typography variant="p" fontSize="14px">
                                        {discount.type}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ width: '100px', textAlign: 'center' }}>
                                    <Switch
                                        checked={discount.status}
                                        onChange={() => handleToggleDiscount(discount.id)}
                                        disabled={claimData?.claimadjudicationStatus == 'Discharge Authorized'}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#4caf50',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                                },
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#4caf50',
                                            },
                                            '& .MuiSwitch-switchBase': {
                                                color: '#f44336',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                                },
                                            },
                                            '& .MuiSwitch-track': {
                                                backgroundColor: '#f44336',
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: 'auto' }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item style={{ width: '150px' }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Amount"
                                                value={discount.amount}
                                                onChange={(e) => handleAmountChange(discount.id, e.target.value)}
                                                disabled={claimData?.claimadjudicationStatus == 'Discharge Authorized'}
                                                fullWidth
                                                error={isAmountInvalid(discount)}
                                                helperText={isAmountInvalid(discount) ? 'Amount cannot be zero' : ''}
                                                FormHelperTextProps={{ style: { fontSize: '0.7rem', margin: '2px 0 0 0' } }}
                                                inputProps={{
                                                    min: 0,
                                                    style: { fontSize: '0.8rem' }
                                                }}
                                                InputProps={{
                                                    style: { fontSize: '0.8rem' }
                                                }}
                                                style={{ background: discount.status ? '#ffffff' : '#f5f5f5' }}
                                            />
                                        </Grid>
                                        <Grid item style={{ width: '120px' }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="%"
                                                value={discount.percentage.toFixed(2)}
                                                disabled
                                                fullWidth
                                                inputProps={{
                                                    style: { fontSize: '0.8rem' }
                                                }}
                                                InputProps={{
                                                    readOnly: true,
                                                    style: { fontSize: '0.8rem' }
                                                }}
                                                style={{ background: '#f5f5f5' }}
                                            />
                                        </Grid>
                                    </Grid>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Grid container justifyContent="flex-end" alignItems="center" gap={2} sx={{ marginTop: 3 }}>
                <Grid item>
                    <LoadingButton
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveDiscounts}
                        size="small"
                        disabled={claimData?.claimadjudicationStatus == 'Discharge Authorized'}
                        loading={isDiscountSubmitting}
                        loadingPosition="start"
                    >
                        Save Changes
                    </LoadingButton>
                </Grid>
                  <Grid item>
                    <Link
                        href="#"
                        underline="hover"
                        sx={{ fontSize: '14px', cursor: 'pointer' }}
                        onClick={handleCMBulk}
                    >
                        See Bulk
                    </Link>
                </Grid>
            </Grid>
        </CardContent>
        </Card>
     )}

        {/* Coverage Result card */}
      <Card sx={{ minWidth: 275 }}>
        {claimData?.pmlClaimResult ? (
          <CardContent>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 900,
                fontFamily: '"Nunito", sans-serif',
              }}
              gutterBottom
            >
              Policy Coverage Results :
            </Typography>
            <Typography variant="h5" component="div"></Typography>
            <Typography
              variant="subTitle1"
              component={"p"}
              sx={{ fontSize: 18, fontWeight: 700 }}
            >
              {claimData?.pmlClaimResult?.claim_result?.coverageReasons
                ?.length > 0
                ? "Passed Rules :"
                : null}
            </Typography>
            <Typography variant="subTitle1" component={"p"}>
              <ol>
                {claimData?.pmlClaimResult?.coverageReasons.map((reason) => (
                  <li key={reason} style={{ color: "green" }}>
                    {reason}
                  </li>
                ))}
              </ol>
            </Typography>
            <Typography
              variant="subTitle1"
              component={"p"}
              sx={{ fontSize: 18, fontWeight: 700 }}
            >
              {claimData?.pmlClaimResult?.coverageFailedReasons?.length > 0
                ? "Applicable Policy Clause for Rejections"
                : null}
            </Typography>
            <Typography variant="subTitle1" component={"p"}>
              <ol>
                {claimData?.pmlClaimResult?.coverageFailedReasons.map(
                  (reason) => (
                    <li key={reason} style={{ color: "red" }}>
                      {reason}
                    </li>
                  )
                )}
              </ol>
            </Typography>
          </CardContent>
        ) : null}
      </Card>
      {claimData.pmlsuccess === false ? (
        <Card sx={{ minWidth: 275 }}>
          <CardContent>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 900,
                fontFamily: '"Nunito", sans-serif',
              }}
              gutterBottom
            >
              Important Dates :
            </Typography>
            <Grid
              className="flex-container"
              style={{
                gap: 20,
                textAlign: "center",
                alignItems: "space-evenly",
              }}
            >
              <Card className="flex-box">
                <Typography
                  variant="body1"
                  component={"p"}
                  sx={{
                    fontSize: "15px",

                                        color: "#B1B3B3",
                                    }}
                                >
                                    Date Of Admission
                                </Typography>
                                <Typography variant="subTitle1" component={"p"}>
                                    {isInsurerUser() ? moment(claimData?.dateOfAdmission).format(
                                        "DD-MM-YYYY"
                                    ) : moment(claimData?.dateOfAdmission).format(
                                        "DD-MM-YYYY, h:mm a"
                                    )}
                                </Typography>
                            </Card>
                            <Card className="flex-box">
                                <Typography
                                    variant="body1"
                                    component={"p"}
                                    sx={{fontSize: "15px", color: "#B1B3B3"}}
                                >
                                    Date Of First Diagnosis
                                </Typography>
                                <Typography variant="subTitle1" component={"p"}>
                                    {isInsurerUser() ? moment(claimData?.dateOfFirstDiagnosis).format(
                                        "DD-MM-YYYY"
                                    ) : moment(claimData?.dateOfFirstDiagnosis).format(
                                        "DD-MM-YYYY, h:mm a"
                                    )}
                                </Typography>
                            </Card>
                            <Card className="flex-box">
                                <Typography
                                    variant="body1"
                                    component={"p"}
                                    sx={{fontSize: "15px", color: "#B1B3B3"}}
                                >
                                    Policy Inception Date
                                </Typography>
                                <Typography variant="subTitle1" component={"p"}>
                                    {isInsurerUser() ? moment(claimData?.policyInceptionDate).format(
                                        "DD-MM-YYYY"
                                    ) : moment(claimData?.policyInceptionDate).format(
                                        "DD-MM-YYYY, h:mm a"
                                    )}
                                </Typography>
                            </Card>
                            <Card className="flex-box">
                                <Typography
                                    variant="body1"
                                    component={"p"}
                                    sx={{fontSize: "15px", color: "#B1B3B3"}}
                                >
                                    Policy Start Date
                                </Typography>
                                <Typography variant="subTitle1" component={"p"}>
                                    {isInsurerUser() ? moment(claimData?.policyStartDate).format(
                                        "DD-MM-YYYY"
                                    ) : moment(claimData?.policyStartDate).format(
                                        "DD-MM-YYYY, h:mm a"
                                    )}
                                </Typography>
                            </Card>
                            <Card className="flex-box">
                                <Typography
                                    variant="body1"
                                    component={"p"}
                                    sx={{fontSize: "15px", color: "#B1B3B3"}}
                                >
                                    Policy End Date
                                </Typography>
                                <Typography variant="subTitle1" component={"p"}>
                                    {isInsurerUser() ? moment(claimData?.policyEndDate).format(
                                        "DD-MM-YYYY"
                                    ) : moment(claimData?.policyEndDate).format(
                                        "DD-MM-YYYY, h:mm a"
                                    )}
                                </Typography>
                            </Card>
                        </Grid>
          </CardContent>
        </Card>
      ) : null}

      {/* CM Bulk Modal */}
      <Dialog
        open={cmBulkModalOpen}
        onClose={handleCloseCmBulkModal}
        maxWidth="80%"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
          CM Bulk Details
          <IconButton
            aria-label="close"
            onClick={handleCloseCmBulkModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isCmBulkLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : cmBulkData ? (
            <Box>
              {/* Summary Section */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Summary</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Bill Tariff Status</Typography>
                  <Typography variant="body1" fontWeight="bold">{cmBulkData.bill_tariff_status || '-'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">PML Status</Typography>
                  <Typography variant="body1" fontWeight="bold">{cmBulkData.pml_status || '-'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Total Bill Amount</Typography>
                  <Typography variant="body1" fontWeight="bold">₹ {cmBulkData.total_bill_amount?.toLocaleString('en-IN') || '0'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Total Tariff Admissible</Typography>
                  <Typography variant="body1" fontWeight="bold">₹ {cmBulkData.total_tariff_admissible_amount?.toLocaleString('en-IN') || '0'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Total PML Admissible</Typography>
                  <Typography variant="body1" fontWeight="bold">₹ {cmBulkData.total_pml_admissible_amount?.toLocaleString('en-IN') || '0'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Total Deduction</Typography>
                  <Typography variant="body1" fontWeight="bold" color="error">₹ {cmBulkData.total_deduction?.toLocaleString('en-IN') || '0'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">Amount After Discount</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">₹ {cmBulkData.amount_after_discount?.toLocaleString('en-IN') || '0'}</Typography>
                </Grid>
              </Grid>

              {/* Category Table */}
              {cmBulkData.billTariffPmlAmountList && cmBulkData.billTariffPmlAmountList.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Category Details</Typography>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Bill Amount</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Tariff Amount</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">MOU %</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">After MOU</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">CM Discount %</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">After CM Discount</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">SPOT Discount %</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">After SPOT</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Final Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cmBulkData.billTariffPmlAmountList.map((category, index) => (
                          <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                            <TableCell>{category.category_name || '-'}</TableCell>
                            <TableCell align="right">₹ {category.bill_requested_amount?.toLocaleString('en-IN') || '0'}</TableCell>
                            <TableCell align="right">₹ {category.tariffAmount?.toLocaleString('en-IN') || '0'}</TableCell>
                            <TableCell align="right">{category.mouApplicable || 0}%</TableCell>
                            <TableCell align="right">₹ {category.amountAfterMou?.toLocaleString('en-IN') || '0'}</TableCell>
                            <TableCell align="right">{category.cm_small_discount_percentage || 0}%</TableCell>
                            <TableCell align="right">₹ {category.amount_after_cm_discount?.toLocaleString('en-IN') || '0'}</TableCell>
                            <TableCell align="right">{category.spot_discount_percentage || 0}%</TableCell>
                            <TableCell align="right">₹ {category.amount_after_spot_discount?.toLocaleString('en-IN') || '0'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>₹ {category.amount_after_discount?.toLocaleString('en-IN') || '0'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Bill Item Details */}
              {cmBulkData.billTariffPmlAmountList && cmBulkData.billTariffPmlAmountList.some(cat => cat.billItemList?.length > 0) && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Line Item Details</Typography>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', maxHeight: '400px' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Item Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Actual Amount</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">MOU Discount %</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">After MOU</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">CM Discount %</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">After CM</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">SPOT Discount %</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">After SPOT</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Final Amount</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Remarks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cmBulkData.billTariffPmlAmountList.flatMap((category, catIndex) =>
                          (category.billItemList || []).map((item, itemIndex) => (
                            <TableRow key={`${catIndex}-${itemIndex}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                              <TableCell>{item.bill_item_name || '-'}</TableCell>
                              <TableCell align="right">₹ {item.actual_amount?.toLocaleString('en-IN') || '0'}</TableCell>
                              <TableCell align="right">{item.mou_discount || 0}%</TableCell>
                              <TableCell align="right">₹ {item.amount_after_mou_discount?.toLocaleString('en-IN') || '0'}</TableCell>
                              <TableCell align="right">{item.cm_small_discount_percentage || 0}%</TableCell>
                              <TableCell align="right">₹ {item.amount_after_cm_discount?.toLocaleString('en-IN') || '0'}</TableCell>
                              <TableCell align="right">{item.spot_discount_percentage || 0}%</TableCell>
                              <TableCell align="right">₹ {item.amount_after_spot_discount?.toLocaleString('en-IN') || '0'}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>₹ {item.amount_after_discount?.toLocaleString('en-IN') || '0'}</TableCell>
                              <TableCell sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.remarks || ''}>
                                {item.remarks || '-'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="body1" color="textSecondary">No data available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCmBulkModal} variant="contained" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
        </Paper>
    );
}

export default PMLTab;
